import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Bell, Building2, ChevronDown, FileText, Folder, Scale, User, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import {
  definirAvisos,
  definirPadrao,
  definirPolitica,
  seguirPadrao,
  type CompartilhamentoDoMembro,
  type ModoPolitica,
  type PoliticaCompartilhamento,
  type TipoCompartilhavel,
} from '@/api/compartilhamento';
import type { EscritorioMembro } from '@/api/client';
import type { Equipe } from '@/api/equipes';
import { chaveDoAlvo, descreverDestinos, DestinosPicker, useDestinos } from '@/components/escritorio/DestinosPicker';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { cn } from '@/lib/utils';

const TIPOS: {
  tipo: TipoCompartilhavel;
  titulo: string;
  ajuda: string;
  icone: ReactNode;
  plural: string;
}[] = [
  {
    tipo: 'PROCESSO',
    titulo: 'Processos',
    ajuda: 'As intimações e os documentos dos autos vão junto, e os clientes do processo também ficam visíveis.',
    icone: <Scale className="h-4 w-4" />,
    plural: 'processos',
  },
  {
    tipo: 'CLIENTE',
    titulo: 'Clientes avulsos',
    ajuda: 'Os clientes que não estão num processo compartilhado (os que estão já seguem o processo).',
    icone: <Users className="h-4 w-4" />,
    plural: 'clientes',
  },
  {
    tipo: 'ARTEFATO',
    titulo: 'Documentos',
    ajuda: 'Os documentos pessoais, fora dos autos. Os do escritório já são de todos.',
    icone: <Folder className="h-4 w-4" />,
    plural: 'documentos',
  },
  {
    tipo: 'MODELO_PETICAO',
    titulo: 'Modelos de petição',
    ajuda: 'Os modelos pessoais, para os colegas lerem e copiarem.',
    icone: <FileText className="h-4 w-4" />,
    plural: 'modelos',
  },
];

/** O modo como a pessoa pensa nele (o "todos" se divide em hoje / também os próximos). */
type Escolha = 'PADRAO' | 'NENHUM' | 'SELECIONADOS' | 'TODOS_HOJE' | 'TODOS_SEMPRE';
const ESCOLHAS_DO_MEMBRO: {
  value: Exclude<Escolha, 'PADRAO'>;
  label: string;
}[] = [
  { value: 'TODOS_SEMPRE', label: 'Todos, inclusive os que chegarem' },
  { value: 'TODOS_HOJE', label: 'Todos os que tenho hoje' },
  { value: 'SELECIONADOS', label: 'Só os que eu escolher' },
  { value: 'NENHUM', label: 'Não compartilhar' },
];
/** O padrão do escritório é uma regra: "os de hoje" é de cada membro, não entra aqui. */
const ESCOLHAS_DO_PADRAO: {
  value: 'TODOS_SEMPRE' | 'SELECIONADOS' | 'NENHUM';
  label: string;
}[] = [
  { value: 'TODOS_SEMPRE', label: 'Todos, inclusive os que chegarem' },
  { value: 'SELECIONADOS', label: 'Só os que cada um escolher' },
  { value: 'NENHUM', label: 'Não compartilhar' },
];

function escolhaDe(p: PoliticaCompartilhamento): Exclude<Escolha, 'PADRAO'> {
  if (p.modo === 'TODOS') return p.incluir_proximos ? 'TODOS_SEMPRE' : 'TODOS_HOJE';
  return p.modo;
}

function politicaDe(escolha: Exclude<Escolha, 'PADRAO'>): {
  modo: ModoPolitica;
  incluir_proximos: boolean;
} {
  if (escolha === 'TODOS_SEMPRE') return { modo: 'TODOS', incluir_proximos: true };
  if (escolha === 'TODOS_HOJE') return { modo: 'TODOS', incluir_proximos: false };
  return { modo: escolha, incluir_proximos: false };
}

const NENHUMA: (tipo: TipoCompartilhavel) => PoliticaCompartilhamento = (tipo) => ({
  recurso_tipo: tipo,
  modo: 'NENHUM',
  incluir_proximos: false,
  alvos: [],
});

/** O padrão em uma frase: "todos, inclusive os que chegarem, com todo o escritório". */
function descreverPadrao(p: PoliticaCompartilhamento, equipes: Equipe[], membros: EscritorioMembro[]): string {
  if (p.modo === 'NENHUM') return 'não compartilhar';
  const com = descreverDestinos(p.alvos, equipes, membros) || 'ninguém';
  if (p.modo === 'SELECIONADOS') return `só os que cada um escolher (com ${com}, ao compartilhar)`;
  return `todos, inclusive os que chegarem, com ${com}`;
}

/** Os dois jeitos de um escritório funcionar (§19); fora deles, o padrão é "personalizado". */
type ModoDoEscritorio = 'CADA_UM' | 'TODOS';
const MODOS_DO_ESCRITORIO: {
  value: ModoDoEscritorio;
  label: string;
  ajuda: string;
}[] = [
  {
    value: 'CADA_UM',
    label: 'Cada um vê o seu',
    ajuda: 'Cada membro vê o próprio acervo e o que um colega compartilhar com ele.',
  },
  {
    value: 'TODOS',
    label: 'Todos veem tudo',
    ajuda:
      'Processos, clientes e modelos de todos, inclusive os que chegarem, com todo o escritório. Os documentos avulsos continuam com cada um.',
  },
];

const SO_O_ESCRITORIO = [{ tipo: 'ESCRITORIO' as const, valor: null }];

/** O padrão de cada tipo em cada modo do escritório. */
function padraoNoModo(modo: ModoDoEscritorio, tipo: TipoCompartilhavel) {
  if (modo === 'TODOS' && tipo !== 'ARTEFATO') return { modo: 'TODOS' as const, incluir_proximos: true };
  return { modo: 'SELECIONADOS' as const, incluir_proximos: false };
}

function ehSoOEscritorio(p: PoliticaCompartilhamento): boolean {
  return p.alvos.length === 1 && p.alvos[0].tipo === 'ESCRITORIO';
}

/** Em qual dos dois modos o padrão está, ou ``null`` quando foi ajustado tipo a tipo. */
function modoDoEscritorio(padraoDe: (tipo: TipoCompartilhavel) => PoliticaCompartilhamento): ModoDoEscritorio | null {
  for (const modo of ['CADA_UM', 'TODOS'] as const) {
    const casa = TIPOS.every(({ tipo }) => {
      const p = padraoDe(tipo);
      const esperado = padraoNoModo(modo, tipo);
      return p.modo === esperado.modo && p.incluir_proximos === esperado.incluir_proximos && ehSoOEscritorio(p);
    });
    if (casa) return modo;
  }
  return null;
}

/** Quem vê o quê mudou: o item, as listas da visão do escritório e a própria aba. */
function invalidarVisao(queryClient: QueryClient, escritorioId: string) {
  for (const chave of [
    queryKeys.escritorios.compartilhamento(escritorioId),
    ['escritorios', 'item'],
    queryKeys.processos.all,
    queryKeys.comunicacoes.all,
    queryKeys.clientes.all,
    queryKeys.documentos.all,
    queryKeys.modelos.all,
  ]) {
    queryClient.invalidateQueries({ queryKey: chave });
  }
}

/** Um interruptor do próprio membro neste escritório, com o que ele significa. */
function Interruptor({
  id,
  icone,
  titulo,
  checked,
  disabled,
  onChange,
  children,
}: {
  id: string;
  icone: ReactNode;
  titulo: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-4 p-4">
        <div className="min-w-0">
          <Label htmlFor={id} className="flex items-center gap-2 font-medium">
            {icone}
            {titulo}
          </Label>
          <p id={`${id}-descricao`} className="mt-1 text-xs text-muted-foreground">
            {children}
          </p>
        </div>
        <Switch
          id={id}
          aria-describedby={`${id}-descricao`}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onChange}
          className="mt-1 shrink-0"
        />
      </CardContent>
    </Card>
  );
}

function Cabecalho({ config, children }: { config: (typeof TIPOS)[number]; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-medium">
          <span className="text-muted-foreground" aria-hidden="true">
            {config.icone}
          </span>
          {config.titulo}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{config.ajuda}</p>
      </div>
      {children}
    </div>
  );
}

interface CartaoProps {
  escritorioId: string;
  config: (typeof TIPOS)[number];
  membros: EscritorioMembro[];
  equipes: Equipe[];
  meuId: string | undefined;
}

/** O padrão do escritório para um tipo: só o Proprietário muda (§18). */
function PadraoCard({
  escritorioId,
  config,
  padrao,
  membros,
  equipes,
  meuId,
}: CartaoProps & { padrao: PoliticaCompartilhamento }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  // O padrão nunca é "os de hoje" (o backend recusa): os outros três valem como estão.
  const [escolha, setEscolha] = useState(escolhaDe(padrao) as (typeof ESCOLHAS_DO_PADRAO)[number]['value']);
  const {
    alvos,
    alternar,
    escolhidos,
    mudou: destinosMudaram,
  } = useDestinos(padrao.modo === 'NENHUM' ? [{ tipo: 'ESCRITORIO' }] : padrao.alvos);
  const mudou = escolha !== escolhaDe(padrao) || (escolha !== 'NENHUM' && destinosMudaram(padrao.alvos));
  const encolheu = escolha === 'TODOS_SEMPRE' && padrao.modo === 'TODOS' && destinosMudaram(padrao.alvos);

  const salvar = useMutation({
    mutationFn: () =>
      definirPadrao(escritorioId, config.tipo, {
        ...politicaDe(escolha),
        alvos: escolha === 'NENHUM' ? [] : escolhidos,
      }),
    onSuccess: (r) => {
      const partes = [
        `Padrão de ${config.plural} salvo; vale para ${r.seguidores} ${r.seguidores === 1 ? 'membro' : 'membros'}`,
      ];
      if (r.retidos_agora > 0) {
        partes.push(`${r.retidos_agora} ${r.retidos_agora === 1 ? 'continua' : 'continuam'} só para leitura`);
      }
      showSuccess(partes.join('; '));
      invalidarVisao(queryClient, escritorioId);
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const semDestino = escolha === 'TODOS_SEMPRE' && escolhidos.length === 0;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <Cabecalho config={config}>
          <Select value={escolha} onValueChange={(v) => setEscolha(v as typeof escolha)}>
            <SelectTrigger className="h-11 w-full sm:h-9 sm:w-64" aria-label={`Padrão do escritório: ${config.titulo}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ESCOLHAS_DO_PADRAO.map((e) => (
                <SelectItem key={e.value} value={e.value}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Cabecalho>
        {escolha !== 'NENHUM' && (
          <DestinosPicker
            id={`padrao-com-quem-${config.tipo}`}
            titulo={
              <>
                Com quem
                {escolha === 'SELECIONADOS' && (
                  <span className="font-normal text-muted-foreground"> (o padrão ao compartilhar um a um)</span>
                )}
              </>
            }
            alvos={alvos}
            onAlternar={alternar}
            equipes={equipes}
            colegas={membros.filter((m) => m.usuario_id !== meuId)}
          />
        )}
        {encolheu && <p className="text-xs text-muted-foreground">Quem sair do “com quem” deixa de ver na hora.</p>}
        {padrao.modo === 'TODOS' && escolha !== 'TODOS_SEMPRE' && (
          <p className="text-xs text-muted-foreground">
            O que os colegas já viam continua com eles, só para ler. Quem escolheu outra coisa não é afetado.
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          {semDestino && <p className="text-xs text-destructive">Escolha com quem compartilhar.</p>}
          <Button
            className="h-11 sm:h-9"
            onClick={() => salvar.mutate()}
            disabled={!mudou || semDestino || salvar.isPending}
          >
            {salvar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            Salvar o padrão
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** O jeito do escritório: a escolha do Proprietário em uma pergunta (§19). */
function ModoDoEscritorioCard({
  escritorioId,
  padraoDe,
}: {
  escritorioId: string;
  padraoDe: (tipo: TipoCompartilhavel) => PoliticaCompartilhamento;
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const atual = modoDoEscritorio(padraoDe);
  const [escolha, setEscolha] = useState<ModoDoEscritorio | null>(atual);

  const salvar = useMutation({
    mutationFn: async (modo: ModoDoEscritorio) => {
      let seguidores: number | null = null;
      let retidos = 0;
      // Um tipo por vez, só o que muda; se um falhar, os anteriores ficam (a aba mostra "personalizado").
      for (const { tipo } of TIPOS) {
        const p = padraoDe(tipo);
        const esperado = padraoNoModo(modo, tipo);
        if (p.modo === esperado.modo && p.incluir_proximos === esperado.incluir_proximos && ehSoOEscritorio(p)) {
          continue;
        }
        const r = await definirPadrao(escritorioId, tipo, {
          ...esperado,
          alvos: SO_O_ESCRITORIO,
        });
        // Quem segue o padrão muda por tipo: o número dito é o dos processos.
        if (tipo === 'PROCESSO') seguidores = r.seguidores;
        retidos += r.retidos_agora;
      }
      return { seguidores, retidos };
    },
    onSuccess: ({ seguidores, retidos }, modo) => {
      const rotulo = MODOS_DO_ESCRITORIO.find((m) => m.value === modo)?.label;
      const partes = [`Escritório: ${rotulo?.toLowerCase()}`];
      if (seguidores) {
        partes.push(`processos: vale para ${seguidores} ${seguidores === 1 ? 'membro' : 'membros'}`);
      }
      if (retidos > 0) partes.push(`${retidos} ${retidos === 1 ? 'continua' : 'continuam'} só para leitura`);
      showSuccess(partes.join('; '));
    },
    onError: (err) => notifyMutationError(err, showError),
    onSettled: () => invalidarVisao(queryClient, escritorioId),
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <RadioGroup
          value={escolha ?? ''}
          onValueChange={(v) => setEscolha(v as ModoDoEscritorio)}
          aria-label="Como o escritório funciona"
          className="gap-3"
        >
          {MODOS_DO_ESCRITORIO.map((m) => (
            <Label
              key={m.value}
              htmlFor={`modo-escritorio-${m.value}`}
              className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal has-[[data-state=checked]]:border-primary"
            >
              <RadioGroupItem id={`modo-escritorio-${m.value}`} value={m.value} className="mt-0.5" />
              <span>
                <span className="block font-medium">{m.label}</span>
                <span className="block text-xs text-muted-foreground">{m.ajuda}</span>
              </span>
            </Label>
          ))}
        </RadioGroup>
        {atual === null && (
          <p className="text-xs text-muted-foreground">O padrão de hoje foi ajustado tipo a tipo (abaixo).</p>
        )}
        {escolha === 'CADA_UM' && TIPOS.some(({ tipo }) => padraoDe(tipo).modo === 'TODOS') && (
          <p className="text-xs text-muted-foreground">
            O que os colegas já viam continua com eles, só para ler. Quem escolheu outra coisa não é afetado.
          </p>
        )}
        {escolha === 'TODOS' && atual !== 'TODOS' && (
          <p className="text-xs text-muted-foreground">
            Todo o escritório passa a ver o acervo de quem segue o padrão. Quem escolheu outra coisa não é afetado.
          </p>
        )}
        <div className="flex justify-end">
          <Button
            className="h-11 sm:h-9"
            onClick={() => escolha && salvar.mutate(escolha)}
            disabled={escolha === null || escolha === atual || salvar.isPending}
          >
            {salvar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            Salvar o padrão
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface PoliticaCardProps extends CartaoProps {
  politica: PoliticaCompartilhamento;
  padrao: PoliticaCompartilhamento;
}

/** O que o membro faz com o próprio acervo de um tipo: seguir o padrão ou escolher outra coisa. */
function PoliticaCard({ escritorioId, config, politica, padrao, membros, equipes, meuId }: PoliticaCardProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const segue = !!politica.segue_padrao;
  const [escolha, setEscolha] = useState<Escolha>(segue ? 'PADRAO' : escolhaDe(politica));
  // Saindo do padrão (ou do "Não compartilhar"), os destinos começam pelos do padrão; com
  // política própria, são os dela — nunca os do padrão em silêncio.
  const partida = padrao.alvos.length > 0 ? padrao.alvos : [{ tipo: 'ESCRITORIO' as const }];
  const {
    alvos,
    alternar,
    escolhidos,
    mudou: destinosMudaram,
  } = useDestinos(segue || politica.modo === 'NENHUM' ? partida : politica.alvos);
  // O destino que havia (uma equipe excluída, uma pessoa que saiu) não existe mais.
  const destinoSumiu = !segue && politica.modo !== 'NENHUM' && politica.alvos.length === 0;
  const mudou =
    escolha === 'PADRAO'
      ? !segue
      : segue || escolha !== escolhaDe(politica) || (escolha !== 'NENHUM' && destinosMudaram(politica.alvos));

  const salvar = useMutation({
    mutationFn: async () => {
      if (escolha === 'PADRAO') {
        const r = await seguirPadrao(escritorioId, config.tipo);
        return { seguiu: true as const, retidos_agora: r.retidos_agora ?? 0 };
      }
      return definirPolitica(escritorioId, config.tipo, {
        ...politicaDe(escolha),
        alvos: escolha === 'NENHUM' ? [] : escolhidos,
      });
    },
    onSuccess: (r) => {
      if ('seguiu' in r) {
        showSuccess(
          r.retidos_agora > 0
            ? `${config.titulo}: seguindo o padrão do escritório; ${r.retidos_agora} ${r.retidos_agora === 1 ? 'continua' : 'continuam'} para os colegas só lerem`
            : `${config.titulo}: seguindo o padrão do escritório`,
        );
      } else {
        const partes = [
          `${config.titulo}: ${r.compartilhados} ${r.compartilhados === 1 ? 'compartilhado' : 'compartilhados'}`,
        ];
        if (r.retidos_agora > 0) {
          partes.push(
            `${r.retidos_agora} ${r.retidos_agora === 1 ? 'continua' : 'continuam'} para os colegas só lerem`,
          );
        }
        showSuccess(partes.join('; '));
      }
      invalidarVisao(queryClient, escritorioId);
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  // Com "os de hoje" salvo, salvar de novo inclui o que chegou desde então.
  const refazerHoje = escolha === 'TODOS_HOJE' && !segue && escolhaDe(politica) === 'TODOS_HOJE';
  const precisaDestino = escolha === 'TODOS_HOJE' || escolha === 'TODOS_SEMPRE';
  const semDestino = precisaDestino && escolhidos.length === 0;
  const colegas = membros.filter((m) => m.usuario_id !== meuId);
  const vigente = segue ? padrao : politica;
  // Para onde a escolha leva: "Seguir o padrão" leva ao modo do padrão.
  const destino = escolha === 'PADRAO' ? padrao.modo : escolha;
  const encolheu =
    !segue &&
    politica.modo === 'TODOS' &&
    (escolha === 'TODOS_SEMPRE' || escolha === 'TODOS_HOJE') &&
    destinosMudaram(politica.alvos);

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <Cabecalho config={config}>
          <Select value={escolha} onValueChange={(v) => setEscolha(v as Escolha)}>
            <SelectTrigger className="h-11 w-full sm:h-9 sm:w-64" aria-label={`O que compartilhar: ${config.titulo}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PADRAO">Seguir o padrão do escritório</SelectItem>
              {ESCOLHAS_DO_MEMBRO.map((e) => (
                <SelectItem key={e.value} value={e.value}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Cabecalho>

        {escolha === 'PADRAO' ? (
          <p className="text-sm text-muted-foreground">
            O padrão do escritório: {descreverPadrao(padrao, equipes, membros)}.
          </p>
        ) : (
          escolha !== 'NENHUM' && (
            <DestinosPicker
              id={`com-quem-${config.tipo}`}
              titulo={
                <>
                  Com quem
                  {escolha === 'SELECIONADOS' && (
                    <span className="font-normal text-muted-foreground"> (o padrão ao compartilhar um a um)</span>
                  )}
                </>
              }
              alvos={alvos}
              onAlternar={alternar}
              equipes={equipes}
              colegas={colegas}
            />
          )
        )}

        {escolha === 'TODOS_HOJE' && (
          <p className="text-xs text-muted-foreground">
            Os {config.plural} que chegarem depois ficam só com você, até que você os compartilhe.
          </p>
        )}
        {destinoSumiu && escolha !== 'PADRAO' && escolha !== 'NENHUM' && (
          <p className="text-xs text-muted-foreground">
            O destino que havia (uma equipe excluída ou uma pessoa que saiu) não existe mais. Escolha com quem.
          </p>
        )}
        {encolheu && <p className="text-xs text-muted-foreground">Quem sair do “com quem” deixa de ver na hora.</p>}
        {vigente.modo === 'TODOS' && (destino === 'NENHUM' || destino === 'SELECIONADOS') && (
          <p className="text-xs text-muted-foreground">
            O que os colegas já viam continua com eles, só para ler. Tirar de vez é um pedido que o Proprietário aprova.
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          {semDestino && <p className="text-xs text-destructive">Escolha com quem compartilhar.</p>}
          <Button
            className="h-11 sm:h-9"
            onClick={() => salvar.mutate()}
            disabled={(!mudou && !refazerHoje) || semDestino || salvar.isPending}
          >
            {salvar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            {refazerHoje && !mudou ? 'Incluir os de hoje' : 'Salvar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface CompartilhamentoAbaProps {
  escritorioId: string;
  dados: CompartilhamentoDoMembro;
  membros: EscritorioMembro[];
  equipes: Equipe[];
  meuId: string | undefined;
  /** Acabou de aceitar o convite: a aba abre com as boas-vindas. */
  bemVindo?: boolean;
}

/**
 * O compartilhamento no escritório (A5, A8, §18). O Proprietário define o padrão do
 * escritório; cada membro segue o padrão ou escolhe outra coisa para o próprio acervo —
 * ninguém decide pelo acervo do colega.
 */
export function CompartilhamentoAba({
  escritorioId,
  dados,
  membros,
  equipes,
  meuId,
  bemVindo,
}: CompartilhamentoAbaProps) {
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const ehDono = dados.meu_role === 'PROPRIETARIO';
  const [porTipoAberto, setPorTipoAberto] = useState(false);
  const avisos = useMutation({
    mutationFn: (recebe: boolean) => definirAvisos(escritorioId, recebe),
    onSuccess: (r) => queryClient.setQueryData(queryKeys.escritorios.compartilhamento(escritorioId), r),
    onError: (err) => notifyMutationError(err, showError),
  });
  // Sem a linha do padrão (escritório anterior a ele), ninguém compartilha por regra: é o
  // "cada um vê o seu" de fábrica, e o backend cria a linha ao salvar.
  const padraoDe = (tipo: TipoCompartilhavel) =>
    (dados.padrao ?? []).find((p) => p.recurso_tipo === tipo) ?? {
      ...NENHUMA(tipo),
      modo: 'SELECIONADOS' as const,
      alvos: [{ tipo: 'ESCRITORIO' as const, valor: null }],
    };
  const chaveDe = (p: PoliticaCompartilhamento) =>
    `${p.recurso_tipo}:${p.segue_padrao ? 'padrao' : ''}:${p.modo}:${p.incluir_proximos}:${p.alvos.map(chaveDoAlvo).sort().join(',')}`;

  return (
    <div className="space-y-6">
      <div className={cn('rounded-md border p-3 text-sm', bemVindo ? 'border-primary/40 bg-primary/5' : 'bg-muted/30')}>
        {bemVindo && <p className="mb-1 font-medium">Bem-vindo ao escritório.</p>}
        {ehDono
          ? 'Escolha como o escritório funciona. Cada membro segue o padrão, a menos que escolha outra coisa para o próprio acervo.'
          : 'O escritório compartilha por padrão o que está abaixo. Você pode seguir o padrão ou escolher outra coisa para o seu acervo, e mudar quando quiser.'}
      </div>

      <Interruptor
        id="avisos-cuidados"
        icone={<Bell className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
        titulo="Avisos dos processos de que você cuida"
        checked={dados.recebe_avisos !== false}
        disabled={avisos.isPending}
        onChange={(v) => avisos.mutate(v)}
      >
        Receba por e-mail as intimações novas dos processos de colegas de que você cuida neste escritório. Os
        prazos deles ficam na sua agenda e no painel de qualquer forma.
      </Interruptor>

      {ehDono && (
        <section className="space-y-3" aria-labelledby="padrao-do-escritorio">
          <h2 id="padrao-do-escritorio" className="flex items-center gap-2 text-base font-semibold">
            <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Padrão do escritório
          </h2>
          <ModoDoEscritorioCard
            key={TIPOS.map(({ tipo }) => chaveDe(padraoDe(tipo))).join('|')}
            escritorioId={escritorioId}
            padraoDe={padraoDe}
          />
          <Button
            variant="ghost"
            className="h-11 px-2 text-muted-foreground sm:h-9"
            aria-expanded={porTipoAberto || modoDoEscritorio(padraoDe) === null}
            aria-controls="padrao-por-tipo"
            onClick={() => setPorTipoAberto((v) => !v)}
            disabled={modoDoEscritorio(padraoDe) === null}
          >
            <ChevronDown
              className={cn(
                'mr-1 h-4 w-4 transition-transform',
                (porTipoAberto || modoDoEscritorio(padraoDe) === null) && 'rotate-180',
              )}
              aria-hidden="true"
            />
            Ajustar por tipo
          </Button>
          <div id="padrao-por-tipo" className="space-y-3">
            {(porTipoAberto || modoDoEscritorio(padraoDe) === null) &&
              TIPOS.map((config) => {
                const padrao = padraoDe(config.tipo);
                return (
                  <PadraoCard
                    key={chaveDe(padrao)}
                    escritorioId={escritorioId}
                    config={config}
                    padrao={padrao}
                    membros={membros}
                    equipes={equipes}
                    meuId={meuId}
                  />
                );
              })}
          </div>
        </section>
      )}

      <section className="space-y-3" aria-labelledby="meu-acervo">
        <h2 id="meu-acervo" className="flex items-center gap-2 text-base font-semibold">
          <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />O seu acervo
        </h2>
        {TIPOS.map((config) => {
          const politica = dados.politicas.find((p) => p.recurso_tipo === config.tipo) ?? {
            ...NENHUMA(config.tipo),
            segue_padrao: true,
          };
          return (
            <PoliticaCard
              // Salvo, o cartão renasce do que o servidor gravou (sem estado velho).
              key={chaveDe(politica)}
              escritorioId={escritorioId}
              config={config}
              politica={politica}
              padrao={padraoDe(config.tipo)}
              membros={membros}
              equipes={equipes}
              meuId={meuId}
            />
          );
        })}
      </section>
    </div>
  );
}
